import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface OAuthButtonsProps {
  onEmailClick?: () => void;
  showEmailButton?: boolean;
}

export default function OAuthButtons({ onEmailClick, showEmailButton = true }: OAuthButtonsProps) {
  const t = useTranslations('consultantAuth.oauth');
  const providers = useTranslations('auth.providers');
  const { signInWithOAuth } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState<'azure' | 'google' | null>(null);

  const handleOAuthSignIn = async (provider: 'azure' | 'google') => {
    setLoadingProvider(provider);
    try {
      await signInWithOAuth(provider);
      // User will be redirected, no need for success toast
    } catch (error) {
      const message = userFacingErrorMessage(error, t('failed'));
      toast.error(message);
      setLoadingProvider(null);
    }
  };

  return (
    <div className="space-y-3">
      <Button
        variant="outline"
        className="w-full"
        onClick={() => handleOAuthSignIn('azure')}
        disabled={loadingProvider !== null}
      >
        {loadingProvider === 'azure' ? (
          t('redirecting')
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0h10.931v10.931H0z" fill="#f25022"/>
              <path d="M12.069 0H23v10.931H12.069z" fill="#7fba00"/>
              <path d="M0 12.069h10.931V23H0z" fill="#00a4ef"/>
              <path d="M12.069 12.069H23V23H12.069z" fill="#ffb900"/>
            </svg>
            {providers('microsoft')}
          </>
        )}
      </Button>

      <Button
        variant="outline"
        className="w-full"
        onClick={() => handleOAuthSignIn('google')}
        disabled={loadingProvider !== null}
      >
        {loadingProvider === 'google' ? (
          t('redirecting')
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {providers('google')}
          </>
        )}
      </Button>

      {showEmailButton && (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{providers('or')}</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={onEmailClick}
            disabled={loadingProvider !== null}
          >
            <Mail className="w-5 h-5 mr-2" />
            {t('email')}
          </Button>
        </>
      )}
    </div>
  );
}
